import React, { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useFamily } from '../../contexts/FamilyContext'
import { useAuth } from '../../contexts/AuthContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../ui/Button'
import Badge from '../ui/Badge'
import Card from '../ui/Card'
import rewardTemplates, { rewardsByAge, rewardCategories, getRandomRewards, getAgeAppropriateRewards, getRewardsByCategory } from '../../data/rewardTemplates'
import toast from 'react-hot-toast'

const { FiX, FiPlus, FiStar, FiGift, FiSearch, FiFilter, FiZap, FiUsers, FiTrendingUp } = FiIcons

const RewardTemplateModal = ({ isOpen, onClose }) => {
  const { createReward } = useFamily()
  const { family } = useAuth()
  const [loading, setLoading] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState('food')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedPointRange, setSelectedPointRange] = useState('all')
  const [selectedAge, setSelectedAge] = useState('all')
  const [selectedRewards, setSelectedRewards] = useState([])

  // Get all available rewards
  const allRewards = useMemo(() => {
    let rewards = []
    
    if (selectedCategory === 'age-appropriate' && selectedAge !== 'all') {
      rewards = getAgeAppropriateRewards(parseInt(selectedAge))
    } else if (selectedCategory === 'point-range' && selectedPointRange !== 'all') {
      rewards = getRewardsByCategory(selectedPointRange)
    } else if (selectedCategory in rewardTemplates) {
      rewards = rewardTemplates[selectedCategory]
    } else if (selectedCategory === 'all') {
      rewards = Object.values(rewardTemplates).flat()
    }

    // Apply search filter
    if (searchTerm) {
      rewards = rewards.filter(reward => 
        reward.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reward.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reward.category.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    return rewards
  }, [selectedCategory, selectedAge, selectedPointRange, searchTerm])

  const categories = [
    { id: 'food', label: 'Food & Treats', icon: '🍦', count: rewardTemplates.food.length },
    { id: 'screenTime', label: 'Screen Time', icon: '📱', count: rewardTemplates.screenTime.length },
    { id: 'activities', label: 'Activities & Outings', icon: '🎯', count: rewardTemplates.activities.length },
    { id: 'social', label: 'Social & Friends', icon: '👫', count: rewardTemplates.social.length },
    { id: 'privileges', label: 'Privileges & Special', icon: '⭐', count: rewardTemplates.privileges.length },
    { id: 'shopping', label: 'Toys & Shopping', icon: '🧸', count: rewardTemplates.shopping.length },
    { id: 'creative', label: 'Creative & Learning', icon: '🎨', count: rewardTemplates.creative.length },
    { id: 'physical', label: 'Physical Activities', icon: '🏃', count: rewardTemplates.physical.length },
    { id: 'events', label: 'Special Events', icon: '🎉', count: rewardTemplates.events.length },
    { id: 'age-appropriate', label: 'Age-Appropriate', icon: FiUsers, count: 0 },
    { id: 'point-range', label: 'By Point Range', icon: FiZap, count: 0 },
    { id: 'all', label: 'All Rewards', icon: FiStar, count: Object.values(rewardTemplates).flat().length }
  ]

  const handleRewardToggle = (reward) => {
    setSelectedRewards(prev => {
      const isSelected = prev.find(r => r.title === reward.title)
      if (isSelected) {
        return prev.filter(r => r.title !== reward.title)
      } else {
        return [...prev, reward]
      }
    })
  }

  const handleAddRewards = async () => {
    if (selectedRewards.length === 0) {
      toast.error('Please select at least one reward to add')
      return
    }

    setLoading(true)
    let successCount = 0
    let errorCount = 0

    try {
      for (const reward of selectedRewards) {
        const rewardData = {
          title: reward.title,
          description: reward.description,
          points_required: reward.points,
          emoji: reward.emoji
        }

        const { error } = await createReward(rewardData)
        if (error) {
          console.error('Error creating reward:', error)
          errorCount++
        } else {
          successCount++
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully added ${successCount} reward${successCount !== 1 ? 's' : ''}! 🎉`)
      }
      if (errorCount > 0) {
        toast.error(`Failed to add ${errorCount} reward${errorCount !== 1 ? 's' : ''}`)
      }

      if (successCount > 0) {
        setSelectedRewards([])
        onClose()
      }
    } catch (error) {
      console.error('Unexpected error adding rewards:', error)
      toast.error('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  const getPointRangeColor = (points) => {
    if (points >= 150) return 'text-purple-500'
    if (points >= 80) return 'text-red-500'
    if (points >= 40) return 'text-orange-500'
    return 'text-green-500'
  }

  const getPointRangeLabel = (points) => {
    if (points >= 150) return 'Mega'
    if (points >= 80) return 'Big'
    if (points >= 40) return 'Medium'
    return 'Quick'
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden"
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  Reward Templates Library
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="p-2"
                >
                  <SafeIcon icon={FiX} className="text-xl" />
                </Button>
              </div>

              {/* Search and Filters */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search rewards..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                {selectedCategory === 'age-appropriate' && (
                  <select
                    value={selectedAge}
                    onChange={(e) => setSelectedAge(e.target.value)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="all">All Ages</option>
                    <option value="4">Ages 3-5</option>
                    <option value="7">Ages 6-8</option>
                    <option value="10">Ages 9-12</option>
                    <option value="15">Ages 13+</option>
                  </select>
                )}

                {selectedCategory === 'point-range' && (
                  <select
                    value={selectedPointRange}
                    onChange={(e) => setSelectedPointRange(e.target.value)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="all">All Point Ranges</option>
                    <option value="quick">Quick Rewards (15-40 pts)</option>
                    <option value="medium">Medium Rewards (40-80 pts)</option>
                    <option value="big">Big Rewards (80-150 pts)</option>
                    <option value="mega">Mega Rewards (150+ pts)</option>
                  </select>
                )}
              </div>
            </div>

            <div className="flex h-[calc(90vh-200px)]">
              {/* Categories Sidebar */}
              <div className="w-80 border-r border-gray-200 dark:border-gray-700 p-4 overflow-y-auto">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Categories</h3>
                <div className="space-y-2">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all duration-200 ${
                        selectedCategory === category.id
                          ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 border-l-4 border-primary-500'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      {typeof category.icon === 'string' ? (
                        <span className="text-xl">{category.icon}</span>
                      ) : (
                        <SafeIcon icon={category.icon} className="text-lg" />
                      )}
                      <div className="flex-1">
                        <div className="font-medium">{category.label}</div>
                        {category.count > 0 && (
                          <div className="text-xs opacity-75">{category.count} rewards</div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Rewards Grid */}
              <div className="flex-1 p-6 overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Available Rewards ({allRewards.length})
                  </h3>
                  {selectedRewards.length > 0 && (
                    <Badge variant="primary" className="flex items-center gap-1">
                      <SafeIcon icon={FiPlus} className="text-sm" />
                      {selectedRewards.length} selected
                    </Badge>
                  )}
                </div>

                {allRewards.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {allRewards.map((reward, index) => {
                      const isSelected = selectedRewards.find(r => r.title === reward.title)
                      return (
                        <motion.div
                          key={`${reward.title}-${index}`}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          whileHover={{ scale: 1.02 }}
                        >
                          <Card
                            className={`cursor-pointer transition-all duration-200 ${
                              isSelected
                                ? 'ring-2 ring-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                : 'hover:shadow-lg'
                            }`}
                            onClick={() => handleRewardToggle(reward)}
                          >
                            <div className="flex items-start gap-3 mb-3">
                              <div className="text-2xl">{reward.emoji}</div>
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                                  {reward.title}
                                </h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                  {reward.description}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 mb-3">
                              <Badge variant="primary" size="sm" className="flex items-center gap-1">
                                <SafeIcon icon={FiGift} className={`text-xs ${getPointRangeColor(reward.points)}`} />
                                {reward.points} pts
                              </Badge>
                              <Badge variant="secondary" size="sm">
                                {getPointRangeLabel(reward.points)}
                              </Badge>
                              <Badge variant="info" size="sm" className="capitalize">
                                {reward.category}
                              </Badge>
                            </div>

                            {isSelected && (
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="flex items-center justify-center p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg"
                              >
                                <SafeIcon icon={FiPlus} className="text-primary-600 dark:text-primary-400" />
                                <span className="text-sm font-medium text-primary-600 dark:text-primary-400 ml-2">
                                  Selected
                                </span>
                              </motion.div>
                            )}
                          </Card>
                        </motion.div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">🔍</div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      No rewards found
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Try adjusting your search or selecting a different category
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedRewards.length > 0 ? (
                    <>Selected {selectedRewards.length} reward{selectedRewards.length !== 1 ? 's' : ''} • 
                    Total: {selectedRewards.reduce((sum, reward) => sum + reward.points, 0)} points</>
                  ) : (
                    'Select rewards to add to your family'
                  )}
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="ghost"
                    onClick={onClose}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleAddRewards}
                    loading={loading}
                    disabled={selectedRewards.length === 0}
                  >
                    <SafeIcon icon={FiPlus} className="text-sm" />
                    Add {selectedRewards.length > 0 ? selectedRewards.length : ''} Reward{selectedRewards.length !== 1 ? 's' : ''}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default RewardTemplateModal